import React, { useState, useEffect, useRef } from 'react';
import { Upload, X, ArrowLeft } from 'lucide-react';
import Button from '../../../components/Button';
import { useAudience } from '../../../context/AudienceContext';

interface SegmentPersonaFilters {
  industryL1: string[];
  industryL2: string[];
  functions: string[];
  roles: string[];
}

interface PackagingReviewV2Props {
  onSubmit: (simulationId: number) => void;
  selectedSegmentIds: number[];
  personaFilters: Record<number, SegmentPersonaFilters>;
  onBack: () => void;
  onEditStep?: () => void;
  initialFormData?: Record<string, any>;
  onFormDataChange?: (data: Record<string, any>) => void;
  onSavePayload?: (payload: Record<string, any>) => void;
}

interface Segment {
  id: number;
  name: string;
  description: string;
  len: number;
  created_at: string;
  updated_at: string;
}

type ImageFile = {
  id: string;
  file: File;
  preview: string;
};

const API_URL = import.meta.env.VITE_API_URL || '';

const PackagingReviewV2Form: React.FC<PackagingReviewV2Props> = ({ 
  onSubmit, 
  selectedSegmentIds, 
  personaFilters, 
  onBack,
  onEditStep,
  initialFormData = {},
  onFormDataChange = () => {},
  onSavePayload,
}) => {
  const { audienceData } = useAudience();
  const [simName, setSimName] = useState(initialFormData.simName || '');
  const [brand, setBrand] = useState(initialFormData.brand || '');
  const [goal, setGoal] = useState(initialFormData.goal || '');
  const [analysisType, setAnalysisType] = useState(initialFormData.analysisType || 'all');
  const [frontPackagingImage, setFrontPackagingImage] = useState<ImageFile[]>([]); // Max 1 - front view (used for all analyses)
  const [sidePackagingImages, setSidePackagingImages] = useState<ImageFile[]>([]); // Side panel images
  const [backPackagingImages, setBackPackagingImages] = useState<ImageFile[]>([]); // Back panel images
  const [competitorImages, setCompetitorImages] = useState<ImageFile[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [segments, setSegments] = useState<Segment[]>([]);
  const frontPackagingFileInputRef = useRef<HTMLInputElement>(null);
  const sidePackagingFileInputRef = useRef<HTMLInputElement>(null);
  const backPackagingFileInputRef = useRef<HTMLInputElement>(null);
  const competitorFileInputRef = useRef<HTMLInputElement>(null);

  const handleEdit = () => {
    if (onEditStep) {
      onEditStep();
    }
  };

  useEffect(() => {
    const fetchSegments = async () => {
      if (!audienceData.audienceId) return;
      
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_URL}/segments?audience_id=${audienceData.audienceId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        if (response.ok) {
          const data = await response.json();
          setSegments(data);
        }
      } catch (error) {
        console.error('Error fetching segments:', error);
      }
    };

    fetchSegments();
  }, [audienceData.audienceId]);

  // Track if we've already loaded images to avoid re-loading
  const imagesLoadedRef = useRef(false);

  // Initialize images from initialFormData (for edit & rerun)
  useEffect(() => {
    // Only load once, and only if we have data
    if (imagesLoadedRef.current) return;

    const hasFrontImage = initialFormData.frontImage;
    const hasSideImages = initialFormData.sideImages && Array.isArray(initialFormData.sideImages) && initialFormData.sideImages.length > 0;
    const hasBackImages = initialFormData.backImages && Array.isArray(initialFormData.backImages) && initialFormData.backImages.length > 0;
    const hasCompetitorImages = initialFormData.competitorImages && Array.isArray(initialFormData.competitorImages) && initialFormData.competitorImages.length > 0;

    if (!hasFrontImage && !hasSideImages && !hasBackImages && !hasCompetitorImages) return;

    imagesLoadedRef.current = true;

    // Helper to load images
    const loadImages = (images: string[], prefix: string): ImageFile[] => {
      return images.map((imageData: string, i: number) => {
        let previewUrl = imageData;
        if (imageData && !imageData.startsWith('data:') && !imageData.startsWith('http')) {
          previewUrl = `${API_URL}${imageData}`;
        }
        return {
          id: `existing-${prefix}-${Date.now()}-${i}`,
          file: new File([], `existing-${prefix}-${i}.jpg`, { type: 'image/jpeg' }),
          preview: previewUrl
        };
      });
    };

    // Load front image
    if (hasFrontImage) {
      const imageData = initialFormData.frontImage;
      let previewUrl = imageData;
      if (imageData && !imageData.startsWith('data:') && !imageData.startsWith('http')) {
        previewUrl = `${API_URL}${imageData}`;
      }
      setFrontPackagingImage([{
        id: `existing-front-${Date.now()}`,
        file: new File([], `existing-front.jpg`, { type: 'image/jpeg' }),
        preview: previewUrl
      }]);
    }

    // Load side images
    if (hasSideImages) {
      setSidePackagingImages(loadImages(initialFormData.sideImages, 'side'));
    }

    // Load back images
    if (hasBackImages) {
      setBackPackagingImages(loadImages(initialFormData.backImages, 'back'));
    }

    // Load competitor images
    if (hasCompetitorImages) {
      setCompetitorImages(loadImages(initialFormData.competitorImages, 'competitor'));
    }
  }, [initialFormData.frontImage, initialFormData.sideImages, initialFormData.backImages, initialFormData.competitorImages]);

  useEffect(() => {
    const formData = {
      simName,
      brand,
      goal,
      analysisType,
    };
    onFormDataChange(formData);
  }, [simName, brand, goal, analysisType, onFormDataChange]);

  type ImageUploadType = 'front-packaging' | 'side-packaging' | 'back-packaging' | 'competitor';

  const handleImageUpload = (
    e: React.ChangeEvent<HTMLInputElement>,
    type: ImageUploadType
  ) => {
    handleEdit();
    const files = Array.from(e.target.files || []);
    const validFiles = files.filter(file =>
      file.type.startsWith('image/') && file.size <= 10 * 1024 * 1024
    );

    const newImages: ImageFile[] = validFiles.map(file => ({
      id: `${Date.now()}-${Math.random()}`,
      file,
      preview: URL.createObjectURL(file)
    }));

    if (type === 'front-packaging') {
      // Limit to 1 front packaging image only
      if (frontPackagingImage.length === 0 && newImages.length > 0) {
        setFrontPackagingImage([newImages[0]]);
        // Clean up extra previews
        newImages.slice(1).forEach(img => URL.revokeObjectURL(img.preview));
      } else if (frontPackagingImage.length > 0) {
        alert('Only 1 front packaging image is allowed. Please remove the existing image first.');
      }
    } else if (type === 'side-packaging') {
      setSidePackagingImages(prev => [...prev, ...newImages]);
    } else if (type === 'back-packaging') {
      setBackPackagingImages(prev => [...prev, ...newImages]);
    } else {
      // Competitor images
      setCompetitorImages(prev => [...prev, ...newImages]);
    }
  };

  const removeImage = (imageId: string, type: ImageUploadType) => {
    handleEdit();
    const removeFromState = (
      setter: React.Dispatch<React.SetStateAction<ImageFile[]>>
    ) => {
      setter(prev => {
        const imageToRemove = prev.find(img => img.id === imageId);
        if (imageToRemove) {
          URL.revokeObjectURL(imageToRemove.preview);
        }
        return prev.filter(img => img.id !== imageId);
      });
    };

    if (type === 'front-packaging') {
      removeFromState(setFrontPackagingImage);
    } else if (type === 'side-packaging') {
      removeFromState(setSidePackagingImages);
    } else if (type === 'back-packaging') {
      removeFromState(setBackPackagingImages);
    } else {
      removeFromState(setCompetitorImages);
    }
  };

  const triggerFileInput = (type: ImageUploadType) => {
    if (type === 'front-packaging') {
      frontPackagingFileInputRef.current?.click();
    } else if (type === 'side-packaging') {
      sidePackagingFileInputRef.current?.click();
    } else if (type === 'back-packaging') {
      backPackagingFileInputRef.current?.click();
    } else {
      competitorFileInputRef.current?.click();
    }
  };

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = error => reject(error);
    });
  };

  // Convert image to base64 - handles both new files and existing images
  const imageToBase64 = async (img: ImageFile): Promise<string> => {
    // If preview is already base64, use it directly
    if (img.preview.startsWith('data:')) {
      return img.preview;
    }
    
    // If it's a URL (existing image from server), fetch and convert
    if (img.preview.startsWith('http') || img.preview.startsWith('/')) {
      try {
        const response = await fetch(img.preview, { credentials: 'include' });
        const blob = await response.blob();
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
      } catch (error) {
        console.error('Error fetching image:', error);
        // Return the URL as-is if fetch fails
        return img.preview;
      }
    }
    
    // Otherwise convert the file
    if (img.file.size > 0) {
      return fileToBase64(img.file);
    }
    
    // Fallback to preview
    return img.preview;
  };

  const saveMode = !!onSavePayload;

  const handleSubmit = async () => {
    // Validate: front packaging image required
    if (!simName.trim() || !brand.trim() || !goal.trim() ||
        frontPackagingImage.length === 0 || competitorImages.length === 0) {
      alert('Please fill in all required fields, upload 1 front packaging image, and at least 1 competitor image');
      return;
    }

    setIsLoading(true);
    try {
      // Convert all images to base64
      const frontImageBase64 = frontPackagingImage.length > 0
        ? await imageToBase64(frontPackagingImage[0])
        : null;

      const sideImagesBase64 = await Promise.all(
        sidePackagingImages.map(img => imageToBase64(img))
      );

      const backImagesBase64 = await Promise.all(
        backPackagingImages.map(img => imageToBase64(img))
      );

      const competitorImagesBase64 = await Promise.all(
        competitorImages.map(img => imageToBase64(img))
      );

      if (onSavePayload) {
        onSavePayload({
          audience_id: audienceData.audienceId,
          segment_ids: selectedSegmentIds,
          persona_filters: personaFilters,
          name: simName,
          description: `Packaging Review V2 - ${brand}`,
          goal_of_simulation: goal,
          message_to_test: '',
          task: 'packaging-review-v2',
          data: {
            brand,
            product_context: '',
            goal,
            placement_type: '',
            design_guideline: '',
            analysis_type: analysisType,
            price_tier: '',
            front_image: frontImageBase64,
            side_images: sideImagesBase64,
            back_images: backImagesBase64,
            competitor_images: competitorImagesBase64,
            segments: segments
              .filter(seg => selectedSegmentIds.includes(seg.id))
              .map(seg => ({ id: seg.id, name: seg.name, description: seg.description }))
          }
        });
        return;
      }

      const token = localStorage.getItem('token');

      const response = await fetch(`${API_URL}/simulations`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          audience_id: audienceData.audienceId,
          segment_ids: selectedSegmentIds,
          persona_filters: personaFilters,
          name: simName,
          description: `Packaging Review V2 - ${brand}`,
          goal_of_simulation: goal,
          message_to_test: '',
          task: 'packaging-review-v2',
          data: {
            brand,
            product_context: '',
            goal,
            placement_type: '',
            design_guideline: '',
            analysis_type: analysisType,
            price_tier: '',
            front_image: frontImageBase64,
            side_images: sideImagesBase64,
            back_images: backImagesBase64,
            competitor_images: competitorImagesBase64,
            segments: segments
              .filter(seg => selectedSegmentIds.includes(seg.id))
              .map(seg => ({ id: seg.id, name: seg.name, description: seg.description }))
          }
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create simulation');
      }

      const data = await response.json();
      onSubmit(data.simulation_id);
    } catch (error) {
      console.error('Error creating simulation:', error);
      alert('Failed to create simulation. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={onBack}
            icon={<ArrowLeft className="w-4 h-4 mr-1" />}
          >
            Back to use case selection
          </Button>
          <span className="text-sm text-gray-500">{selectedSegmentIds.length} segments selected</span>
        </div>
      </div>

      <div className="mb-6">
        <label htmlFor="simName" className="block text-sm font-medium text-gray-700 mb-2">
          Simulation Name <span className="text-red-500">*</span>
        </label>
        <input
          id="simName"
          type="text"
          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          value={simName}
          onChange={(e) => {
            setSimName(e.target.value);
            handleEdit();
          }}
          placeholder="Enter a name for your simulation"
        />
      </div>

      <div className="mb-6">
        <label htmlFor="brand" className="block text-sm font-medium text-gray-700 mb-2">
          Brand Name <span className="text-red-500">*</span>
        </label>
        <input
          id="brand"
          type="text"
          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          value={brand}
          onChange={(e) => {
            setBrand(e.target.value);
            handleEdit();
          }}
          placeholder="Enter your brand name"
        />
      </div>

      <div className="mb-6">
        <label htmlFor="goal" className="block text-sm font-medium text-gray-700 mb-2">
          Goal <span className="text-red-500">*</span>
        </label>
        <input
          id="goal"
          type="text"
          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          value={goal}
          onChange={(e) => {
            setGoal(e.target.value);
            handleEdit();
          }}
          placeholder="What are you trying to achieve with this packaging review?"
        />
      </div>

      {/* Front Packaging Image Upload - Required */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-2">
          <label className="block text-sm font-medium text-gray-700">
            Front Packaging Image <span className="text-red-500">*</span>
            <span className="block text-xs text-gray-500 font-normal mt-0.5">Upload one clear, straight-on photo of the front of your packaging</span>
          </label>
          {frontPackagingImage.length === 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => triggerFileInput('front-packaging')}
              aria-label="Add front packaging image"
              title="Add front packaging image"
            >
              <Upload className="w-4 h-4 mr-1" /> Add Image
            </Button>
          )}
          <input
            type="file"
            ref={frontPackagingFileInputRef}
            onChange={(e) => handleImageUpload(e, 'front-packaging')}
            accept="image/png,image/jpeg,image/jpg"
            className="hidden"
            aria-label="Upload front packaging image"
            title="Upload front packaging image"
          />
        </div>

        {frontPackagingImage.length === 0 ? (
          <div
            className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-blue-500 transition-colors"
            onClick={() => triggerFileInput('front-packaging')}
          >
            <Upload className="w-8 h-8 mx-auto text-gray-400" />
            <p className="mt-2 text-sm text-gray-500">
              Click to upload or drag and drop
            </p>
            <p className="text-xs text-gray-400">
              PNG, JPG, GIF up to 10MB (1 image only)
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {frontPackagingImage.map((image) => (
              <div key={image.id} className="relative">
                <img
                  src={image.preview}
                  alt="Front packaging"
                  className="h-32 w-full object-cover rounded-md border border-gray-300"
                />
                <button
                  type="button"
                  aria-label="Remove front packaging image"
                  title="Remove front packaging image"
                  className="absolute top-1 right-1 bg-black bg-opacity-50 rounded-full p-1 text-white hover:bg-opacity-70"
                  onClick={() => removeImage(image.id, 'front-packaging')}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Side Panel Images Upload - Optional */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-2">
          <label className="block text-sm font-medium text-gray-700">
            Side Packaging Images <span className="text-xs text-gray-500">(Optional)</span>
            <span className="block text-xs text-gray-500 font-normal mt-0.5">Upload one image per side panel</span>
          </label>
          <Button
            variant="outline"
            size="sm"
            onClick={() => triggerFileInput('side-packaging')}
            aria-label="Add side panel images"
            title="Add side panel images"
          >
            <Upload className="w-4 h-4 mr-1" /> Add Images
          </Button>
          <input
            type="file"
            ref={sidePackagingFileInputRef}
            onChange={(e) => handleImageUpload(e, 'side-packaging')}
            accept="image/png,image/jpeg,image/jpg"
            multiple
            className="hidden"
            aria-label="Upload side panel images"
            title="Upload side panel images"
          />
        </div>

        {sidePackagingImages.length === 0 ? (
          <div
            className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-blue-500 transition-colors"
            onClick={() => triggerFileInput('side-packaging')}
          >
            <Upload className="w-6 h-6 mx-auto text-gray-400" />
            <p className="mt-2 text-sm text-gray-500">
              Click to upload side panel images (optional)
            </p>
            <p className="text-xs text-gray-400">
              PNG, JPG, GIF up to 10MB each
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {sidePackagingImages.map((image) => (
              <div key={image.id} className="relative">
                <img
                  src={image.preview}
                  alt="Side panel"
                  className="h-32 w-full object-cover rounded-md border border-gray-300"
                />
                <button
                  type="button"
                  aria-label="Remove side panel image"
                  title="Remove side panel image"
                  className="absolute top-1 right-1 bg-black bg-opacity-50 rounded-full p-1 text-white hover:bg-opacity-70"
                  onClick={() => removeImage(image.id, 'side-packaging')}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}

            <div
              className="h-32 border-2 border-dashed border-gray-300 rounded-md flex flex-col items-center justify-center cursor-pointer hover:border-blue-500 transition-colors"
              onClick={() => triggerFileInput('side-packaging')}
            >
              <Upload className="w-6 h-6 text-gray-400" />
              <span className="mt-1 text-xs text-gray-500">Add more</span>
            </div>
          </div>
        )}
      </div>

      {/* Back Panel Images Upload - Optional */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-2">
          <label className="block text-sm font-medium text-gray-700">
            Back Packaging Images <span className="text-xs text-gray-500">(Optional)</span>
            <span className="block text-xs text-gray-500 font-normal mt-0.5">Upload one image per back panel</span>
          </label>
          <Button
            variant="outline"
            size="sm"
            onClick={() => triggerFileInput('back-packaging')}
            aria-label="Add back panel images"
            title="Add back panel images"
          >
            <Upload className="w-4 h-4 mr-1" /> Add Images
          </Button>
          <input
            type="file"
            ref={backPackagingFileInputRef}
            onChange={(e) => handleImageUpload(e, 'back-packaging')}
            accept="image/png,image/jpeg,image/jpg"
            multiple
            className="hidden"
            aria-label="Upload back panel images"
            title="Upload back panel images"
          />
        </div>

        {backPackagingImages.length === 0 ? (
          <div
            className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-blue-500 transition-colors"
            onClick={() => triggerFileInput('back-packaging')}
          >
            <Upload className="w-6 h-6 mx-auto text-gray-400" />
            <p className="mt-2 text-sm text-gray-500">
              Click to upload back panel images (optional)
            </p>
            <p className="text-xs text-gray-400">
              PNG, JPG, GIF up to 10MB each
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {backPackagingImages.map((image) => (
              <div key={image.id} className="relative">
                <img
                  src={image.preview}
                  alt="Back panel"
                  className="h-32 w-full object-cover rounded-md border border-gray-300"
                />
                <button
                  type="button"
                  aria-label="Remove back panel image"
                  title="Remove back panel image"
                  className="absolute top-1 right-1 bg-black bg-opacity-50 rounded-full p-1 text-white hover:bg-opacity-70"
                  onClick={() => removeImage(image.id, 'back-packaging')}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}

            <div
              className="h-32 border-2 border-dashed border-gray-300 rounded-md flex flex-col items-center justify-center cursor-pointer hover:border-blue-500 transition-colors"
              onClick={() => triggerFileInput('back-packaging')}
            >
              <Upload className="w-6 h-6 text-gray-400" />
              <span className="mt-1 text-xs text-gray-500">Add more</span>
            </div>
          </div>
        )}
      </div>

      {/* Competitor Images Upload */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-2">
          <label className="block text-sm font-medium text-gray-700">
            Competitor Packaging Images <span className="text-red-500">*</span>
            <span className="block text-xs text-gray-500 font-normal mt-0.5">Upload one image per competitor</span>
          </label>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => triggerFileInput('competitor')}
            aria-label="Add competitor images"
            title="Add competitor images"
          >
            <Upload className="w-4 h-4 mr-1" /> Add Images
          </Button>
          <input
            type="file"
            ref={competitorFileInputRef}
            onChange={(e) => handleImageUpload(e, 'competitor')}
            accept="image/png,image/jpeg,image/jpg"
            multiple
            className="hidden"
            aria-label="Upload competitor images"
            title="Upload competitor images"
          />
        </div>
        
        {competitorImages.length === 0 ? (
          <div 
            className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-blue-500 transition-colors"
            onClick={() => triggerFileInput('competitor')}
          >
            <Upload className="w-8 h-8 mx-auto text-gray-400" />
            <p className="mt-2 text-sm text-gray-500">
              Click to upload or drag and drop
            </p>
            <p className="text-xs text-gray-400">
              PNG, JPG, GIF up to 10MB
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {competitorImages.map((image) => (
              <div key={image.id} className="relative">
                <img
                  src={image.preview}
                  alt={`Competitor ${image.id}`}
                  className="h-32 w-full object-cover rounded-md border border-gray-300"
                />
                <button
                  type="button"
                  aria-label={`Remove image`}
                  title={`Remove image`}
                  className="absolute top-1 right-1 bg-black bg-opacity-50 rounded-full p-1 text-white hover:bg-opacity-70"
                  onClick={() => removeImage(image.id, 'competitor')}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
            
            <div
              className="h-32 border-2 border-dashed border-gray-300 rounded-md flex flex-col items-center justify-center cursor-pointer hover:border-blue-500 transition-colors"
              onClick={() => triggerFileInput('competitor')}
            >
              <Upload className="w-6 h-6 text-gray-400" />
              <span className="mt-1 text-xs text-gray-500">Add more</span>
            </div>
          </div>
        )}

      </div>
      
      <div className="flex justify-end">
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={!simName.trim() || !brand.trim() || !goal.trim() ||
                   frontPackagingImage.length === 0 || competitorImages.length === 0 || isLoading}
          withArrow
        >
          {isLoading ? 'Running Review...' : saveMode ? 'Continue' : 'Run Packaging Review'}
        </Button>
      </div>
    </div>
  );
};

export default PackagingReviewV2Form;

